import React from 'react'
import styled, { css } from 'styled-components'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ButtonUnstyled } from 'components/Button'
import { tabStyle } from 'components/Tabs/Tab'
import { Truncate } from 'components/Truncate'
import arrowDownIcon from 'icons/chevron-vertical-12.svg'
import type { ComponentsTree } from '../../types'

/**
 * TYPES
 */
interface SidebarListProps {
  componentsTree: ComponentsTree[]
  level: number
  onSelect: (path: string) => void
  selected: string
}

/**
 * STYLES
 */
const SidebarLink = styled.a.attrs({ href: '#' })<{
  selected?: boolean
  level: number
  onClick: () => void
}>(
  ({ theme, level, selected }) => css`
    ${tabStyle};

    width: 100%;
    padding-left: calc(${8 * level}px + 12px);

    display: flex;
    justify-content: space-between;
    align-items: center;

    text-align: left;
    white-space: normal;
    cursor: ${selected ? 'default' : 'pointer'};
    line-height: 1.3;

    &:hover {
      background: ${selected ? undefined : theme.colors.background.tertiary[1]};
    }
  `
)

const CollapsibleIcon = styled(ButtonUnstyled)<{ selected?: boolean }>(
  ({ theme, selected }) => css`
    position: relative;
    flex: none;
    width: 40px;
    height: 32px;
    margin: -8px -12px -8px 0;
    border-radius: 6px;

    &:hover {
      background-color: ${theme.colors.background.tertiary[1]};
    }

    &:before {
      position: absolute;
      content: '';
      width: 12px;
      height: 12px;
      transform: translate(-50%, -50%);
      mask: url(${arrowDownIcon}) no-repeat 50% 50%;
      mask-size: cover;
      background-color: ${selected
        ? theme.colors.primaryTint[100]
        : theme.colors.foreground.secondary[4]};
      transition-timing-function: ease-in-out;
      transition-duration: 0.3s;
      transition-property: transform;
      will-change: transform;
    }

    [data-state='closed'] &:before {
      transform: translate(-50%, -50%) rotate(-90deg);
    }
  `
)

/**
 * COMPONENT
 */
export const SidebarList: React.FC<SidebarListProps> = ({
  componentsTree,
  level,
  onSelect,
  selected,
}) => (
  <ul>
    {level === 0 && (
      <li>
        <SidebarLink
          level={level}
          selected={selected === 'View All'}
          onClick={() => onSelect('View All')}>
          View All
        </SidebarLink>
      </li>
    )}
    {componentsTree.map(leaf => (
      <Collapsible.Root
        key={leaf.name}
        asChild
        defaultOpen={selected.includes(leaf.name) && selected !== leaf.path}>
        <li>
          <SidebarLink
            level={level}
            selected={selected === leaf.path}
            onClick={() => onSelect(leaf.path)}>
            <Truncate>{leaf.name}</Truncate>
            {leaf.children?.length ? (
              <Collapsible.Trigger asChild>
                <CollapsibleIcon
                  selected={selected === leaf.path}
                  onClick={e => e.stopPropagation()}
                />
              </Collapsible.Trigger>
            ) : null}
          </SidebarLink>
          {leaf.children?.length ? (
            <Collapsible.Content>
              <SidebarList
                level={level + 1}
                componentsTree={leaf.children}
                onSelect={onSelect}
                selected={selected}
              />
            </Collapsible.Content>
          ) : null}
        </li>
      </Collapsible.Root>
    ))}
  </ul>
)
